// PlayerRow.jsx
import React from "react";
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { followPlayer, unfollowPlayer } from '../../../features/coach/coachProfileSlice';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';
import profileIcon from '../../../assets/images/profile.png';
import { openOnRowClick } from '../../../utils/rowClick';

export const PlayerRow = ({ player, isPlayerView = false, dynamicStatColumns = [], getStatValue = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);



  
  const handleViewProfile = () => {
    // Use player ID to navigate
    const playerId = player._id;

 

    if (isPlayerView) {
      // Player viewing another player's profile - pass complete player data including team in state
      navigate(`/player/profile/${playerId}`, {
        state: {
          from: 'team',
          playerData: {
            ...player,
            // Ensure team data is included
            team: player.team
          }
        }
      });
    } else {
      // Coach viewing player's profile - pass complete player data including team in state
      navigate(`/coach/player/${playerId}`, {
        state: {
          from: 'teams',
          playerData: {
            ...player,
            // Ensure team data is included
            team: player.team
          }
        }
      });
    }
  };

  const handleFollowToggle = (e) => {
    e.stopPropagation();
    const playerId = player._id;
    if (player.isFollowing) {
      dispatch(unfollowPlayer(playerId));
    } else {
      dispatch(followPlayer(playerId));
    }
  };

  // Helper function to safely extract value from MongoDB Decimal128
  const extractDecimalValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object' && value.$numberDecimal) {
      const parsed = parseFloat(value.$numberDecimal);
      return isNaN(parsed) ? null : parsed;
    }
    if (typeof value === 'number') return isNaN(value) ? null : value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  // Extract data from API response structure
  const playerName = player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'N/A';
  const jerseyNumber = player.jerseyNumber || '-';

  // Handle position - prefer primaryPosition over position
  const position = player.primaryPosition || player.position || 'N/A';

  // Handle class - API uses playerClass
  const className = normalizePlayerClass(player.playerClass || player.class) || 'N/A';

  const height = player.height || 'N/A';
  const weight = extractDecimalValue(player.weight) || player.weight || 'N/A';

  // Handle bat/throw - API uses batsThrows
  const batThrow = player.batsThrows || player.batThrow || 'N/A';

  const profileImage = player.profileImage || profileIcon;

  // Get finalScore from battingStats as playerScore - show "NA" if not available
  const playerScore = (() => {
    const stats = Array.isArray(player.battingStats) ? player.battingStats[0] : player.battingStats;
    const score = stats?.finalScore;
    if (score === null || score === undefined || score === '') return 'NA';
    return score;
  })();

  return (
    <tr onClick={openOnRowClick(handleViewProfile)} className="cursor-pointer hover:bg-jp-section transition border-b border-jp-line">
      <td className="px-3 sm:px-4 py-3 sm:py-4 text-jp-muted text-xs sm:text-sm whitespace-nowrap text-center">
        {jerseyNumber}
      </td>

      <td className="px-3 sm:px-4 py-3 sm:py-4">
        <img
          src={profileImage}
          alt="photo"
          className="w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full border border-jp-line object-cover"
          onError={(e) => {
            e.target.src = profileIcon;
          }}
        />
      </td>

      <td className="px-3 sm:px-4 py-3 sm:py-4 text-jp-ink font-semibold text-xs sm:text-sm whitespace-nowrap">
        {playerName}
      </td>
      <td className="px-3 sm:px-4 py-3 sm:py-4 text-jp-muted text-xs sm:text-sm whitespace-nowrap">
        {position}
      </td>
      <td className="px-3 sm:px-4 py-3 sm:py-4 text-jp-muted text-xs sm:text-sm whitespace-nowrap">
        {className}
      </td>
      <td className="px-3 sm:px-4 py-3 sm:py-4 text-jp-muted text-xs sm:text-sm whitespace-nowrap">
        {height}
      </td>
      <td className="px-3 sm:px-4 py-3 sm:py-4 text-jp-muted text-xs sm:text-sm whitespace-nowrap">
        {weight}
      </td>
      <td className="px-3 sm:px-4 py-3 sm:py-4 text-jp-muted text-xs sm:text-sm whitespace-nowrap">
        {batThrow}
      </td>
       {/* <td className="px-3 sm:px-4 py-3 sm:py-4 text-jp-accent-ink font-semibold text-xs sm:text-sm whitespace-nowrap">
        {playerScore}
      </td> */}

      {/* Dynamic stat columns */}
      {dynamicStatColumns.map((column, idx) => (
        <td key={`stat-${idx}`} className="px-3 sm:px-4 py-3 sm:py-4 text-jp-muted text-center text-xs sm:text-sm whitespace-nowrap">
          {getStatValue ? getStatValue(player, column) : '0'}
        </td>
      ))}

      <td className="px-3 sm:px-4 py-3 sm:py-4 sticky right-0 bg-jp-card z-10">
        <div className={`flex flex-row gap-1 sm:gap-2 ${isPlayerView ? 'min-w-[120px]' : 'min-w-[200px] sm:min-w-[220px]'}`}>
          <button
            onClick={handleViewProfile}
            className="border border-jp-line bg-jp-section text-jp-ink py-1 px-2 sm:px-3 rounded-md text-xs sm:text-sm leading-tight hover:bg-jp-line transition whitespace-nowrap"
          >
            View profile
          </button>
          {!isPlayerView && (
            <button
              onClick={handleFollowToggle}
              className={`${
                player.isFollowing
                  ? 'bg-gray-600 hover:bg-gray-700'
                  : 'bg-cyan-500 hover:bg-cyan-400'
              } text-white font-semibold py-1 px-2 sm:px-3 rounded-md text-xs sm:text-sm leading-tight transition whitespace-nowrap`}
            >
              {player.isFollowing ? 'Unfollow' : 'Follow'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
